'use client';

import { FC, useState } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { UserUtils } from '@ravago/shared/next-data-access/client';
import { AccountInformationFormValue, EmailFormValue, RegistrationStep, UserRegistrationRequest } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { LinearStepper } from '@ravago/shared/radiance/components/stepper/components/LinearStepper/LinearStepper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useReCaptcha } from 'next-recaptcha-v3';
import { usePathname, useSearchParams } from 'next/navigation';

import { getLanguageCode } from '../../../../utils/locale/locale.utils';

import { AccountInformationStep } from './account-information-step';
import { ConfirmRegistration } from './confim-registration';
import { EmailAddressStep } from './email-address-step';

interface Props {
   locale: string;
}

export const Registration: FC<Props> = ({ locale }) => {
   const languageCode = getLanguageCode(locale);
   const pathname = usePathname();
   const searchParams = useSearchParams();
   const { login } = useAuthentication();
   const { executeRecaptcha } = useReCaptcha();
   const translate = useTranslations();

   const steps: Array<RegistrationStep> = [RegistrationStep.EMAIL_ADDRESS, RegistrationStep.ACCOUNT_INFO];
   const translatedSteps: Array<string> = [translate(RegistrationStep.EMAIL_ADDRESS), translate(RegistrationStep.ACCOUNT_INFO)];

   const [currentStep, setCurrentStep] = useState<RegistrationStep>(RegistrationStep.EMAIL_ADDRESS);
   const [showRegistrationAlert, setShowRegistrationAlert] = useState(false);
   const [accountInfoFormValue, setAccontInfoFormValue] = useState<AccountInformationFormValue | undefined>();
   const [emailAddressFormValue, setEmailAddressFormValue] = useState<EmailFormValue | undefined>();

   const handleChangeStep = (step: RegistrationStep) => {
      setCurrentStep(step);
   };

   const handleLogin = () => {
      if (typeof window !== 'undefined') {
         localStorage.setItem('navigationPendingScrollPosition', window.scrollY.toString());
      }

      const fullUrl = `${pathname}?${searchParams.toString()}`;

      login(fullUrl);
   };

   const onAccountInfoSubmit = async (accountInfoValue: AccountInformationFormValue) => {
      setAccontInfoFormValue(accountInfoValue);

      if (!emailAddressFormValue || !accountInfoValue) return;

      const registrationForm = {
         ...accountInfoValue,
         email: emailAddressFormValue.email
      };

      const userRegistration: UserRegistrationRequest = {
         firstName: registrationForm.firstName,
         lastName: registrationForm.lastName,
         email: registrationForm.email,
         language: languageCode,
         company: {
            name: registrationForm.companyName,
            countryIsoCode: registrationForm.country.id,
            phoneNumber: registrationForm.phone.phone
         },
         visit: {
            visitorId: '',
            utm: {
               source: '',
               medium: '',
               campaign: '',
               content: ''
            }
         }
      };

      try {
         const captchaToken = await executeRecaptcha('NG_submitRegistration');
         const success = await UserUtils.registerUser(userRegistration, captchaToken);

         if (success) {
            setCurrentStep(RegistrationStep.CONFIRM_REGISTRATION);
         } else {
            setShowRegistrationAlert(true);
         }
      } catch (error) {
         // eslint-disable-next-line no-console
         console.error('Registration failed:', error);
         setShowRegistrationAlert(true);
      }
   };

   const progressBar = (
      <Box height="200px" items="stretch">
         <Grid>
            <GridCol spanDesktop={8} spanTablet={8} spanPhone={4} startDesktop={3}>
               <Box direction="column" gap="lg">
                  <Typography component="h2" variant="heading-1">
                     {translate('auth.register')}
                  </Typography>
                  <LinearStepper steps={translatedSteps} activeStep={steps.indexOf(currentStep)} readOnly />
               </Box>
            </GridCol>
         </Grid>
      </Box>
   );

   return (
      <Box direction="column" gap="xl">
         {currentStep !== RegistrationStep.CONFIRM_REGISTRATION && progressBar}
         <Grid>
            <GridCol spanDesktop={8} spanTablet={8} spanPhone={4} startDesktop={3}>
               <Box direction="column" gap="lg">
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  {currentStep !== RegistrationStep.CONFIRM_REGISTRATION && <Link onClick={handleLogin}>{translate('auth.back-login')}</Link>}
                  <Box direction="column" gap="2xs">
                     {currentStep === RegistrationStep.EMAIL_ADDRESS && (
                        <>
                           <Typography component="h3" variant="heading-2" color="strong">
                              {translate('auth.email-address-title')}
                           </Typography>
                           <Typography component="p" color="subtle">
                              {translate('auth.email-address-description')}
                           </Typography>
                        </>
                     )}
                     {currentStep === RegistrationStep.ACCOUNT_INFO && (
                        <>
                           <Typography component="h3" variant="heading-2" color="strong">
                              {translate('auth.account-info-title')}
                           </Typography>
                           <Typography component="p" color="subtle">
                              {translate('auth.account-info-description')}
                           </Typography>
                        </>
                     )}
                  </Box>
                  <Box direction="column" gap="lg">
                     {currentStep === RegistrationStep.EMAIL_ADDRESS && (
                        <EmailAddressStep
                           handleChangeStep={handleChangeStep}
                           defaultFormValue={emailAddressFormValue}
                           handleSubmitEmailForm={setEmailAddressFormValue}
                           handleLogin={handleLogin}
                        />
                     )}
                     {currentStep === RegistrationStep.ACCOUNT_INFO && (
                        <AccountInformationStep
                           defaultFormValue={accountInfoFormValue}
                           locale={locale}
                           showRegistrationAlert={showRegistrationAlert}
                           handleSubmitAccountInfoForm={onAccountInfoSubmit}
                           handleChangeStep={handleChangeStep}
                        />
                     )}
                     {currentStep === RegistrationStep.CONFIRM_REGISTRATION && <ConfirmRegistration handleLogin={handleLogin} />}
                  </Box>
               </Box>
            </GridCol>
         </Grid>
      </Box>
   );
};
